import {
  InlineEditorLogic,
  isEditMode,
  MergeOrUpdateStateAction,
  useInlineEditor
} from '@tableau/vizportalclient-common-components';
import { ButtonType, ButtonWidget, TabStyles, TextAreaWidget } from '@tableau/widgets';
import React from 'react';
import { style } from 'typestyle';

import { FormattingHints } from '~common/react/components/FormattingHints';
import { Formatters, Messages } from '~src/codegen/Localize';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { rejectIfResultContainsError } from '~src/httpClient/utils/HttpErrorChecker';
import { MarkupField } from '~src/spacesPlatform/components/Explorer/widgets/MarkupField';

import { ACTION_DIALOG_ID } from './ContentDetailsDialog';
import { ContentDetailSection, DetailEditButton, DetailSaveCancelButtons } from './ContentDetailSection';


export const COLLECTION_DESCRIPTION_MAX_LENGTH: number = 255;
export const DESCRIPTION_MAX_LENGTH: number = 4000;

// ID for accessibilty purposes
const sectionHeaderId = ACTION_DIALOG_ID+'-About';

export interface DescriptionDetailSectionProps {
  readonly description?: string;
  readonly isActionEnabled: boolean;
  readonly descriptionMaxLength: number;
  readonly setDescription: (description: string) => Promise<IResult>;
  readonly showPreviewAndFormattingHints?: boolean;
  readonly showProjectImageHint?: boolean;
  readonly incrementRouterSpaceReactKey: () => void;
}

export interface DescriptionDetailEditorState {
  description: string;
  previewMode: boolean;
}

export function DescriptionDetailSection(props: DescriptionDetailSectionProps): JSX.Element {

  const initialEditorState: DescriptionDetailEditorState = {
    description: props.description || '',
    previewMode: false,
  };

  const onSave = React.useCallback(
    async (editorState: DescriptionDetailEditorState) => {
      if (editorState.description === props.description) {
        return;
      }

      await props.setDescription(editorState.description)
        .then(rejectIfResultContainsError)
        .then(props.incrementRouterSpaceReactKey);
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      props.description,
      props.setDescription,
      props.incrementRouterSpaceReactKey
    ]
  );

  const canSave = (editorState: DescriptionDetailEditorState) => {
    if(editorState?.description === initialEditorState.description) {
      return false;
    }

    const descriptionLength = editorState?.description?.length ?? 0;
    return descriptionLength <= props.descriptionMaxLength;
  };

  const logic: InlineEditorLogic<DescriptionDetailEditorState> = useInlineEditor<DescriptionDetailEditorState>({
    editorStateWhenEnteringEditMode: initialEditorState,
    canEdit: props.isActionEnabled,
    onSave: onSave,
    canSaveFn: canSave,
  });

  if (isEditMode(logic)) {
    return (
      <ContentDetailSection
        header={Messages.about()}
        testId={'about'}
        headerId={sectionHeaderId}
        detail = {
          <DescriptionEditMode
            showPreviewAndFormattingHints={props.showPreviewAndFormattingHints}
            showProjectImageHint={props.showProjectImageHint}
            inPreviewMode={logic.editorState.previewMode}
            displayedDescription={logic.editorState.description}
            setEditorState={logic.setEditorStateWithinEditMode}
            disableEditor={logic.isActionBusy}
            descriptionMaxLength={props.descriptionMaxLength}
          />
        }
        isActionAllowed={true}
        action = {
          <>
            {props.showPreviewAndFormattingHints &&
            <PreviewToggleButton
              inPreviewMode={logic.editorState.previewMode}
              setEditorState={logic.setEditorStateWithinEditMode}
              disableButtons={logic.isActionBusy}
            />}
            <DetailSaveCancelButtons
              inlineEditorLogic={logic}
            />
          </>
        }
      />
    );
  }
  else {
    return (
      <ContentDetailSection
        header={Messages.about()}
        testId={'about'}
        detail = {
          <DescriptionReadingMode displayedDescription={props.description || ''} />
        }
        isActionAllowed={logic.canEdit}
        action = {
          <DetailEditButton
            buttonRef={logic.editButtonRef}
            buttonLabel={Messages.edit()}
            buttonTestId={'edit-description'}
            onClickAction={logic.enterEditMode}
          />
        }
      />
    );
  }
}


export interface DescriptionReadingModeProps {
  readonly displayedDescription: string;
}

export function DescriptionReadingMode(props: DescriptionReadingModeProps): JSX.Element {
  return (
    <div style={{paddingTop: TabStyles.Spacing.SmallSpace,}}>
      <MarkupField testId={'detail-dialog-description'} markup={props.displayedDescription} />
    </div>
  );
}


export interface DescriptionEditModeProps {
  readonly showPreviewAndFormattingHints: boolean;
  readonly showProjectImageHint: boolean;
  readonly inPreviewMode: boolean;
  readonly displayedDescription: string;
  readonly setEditorState: React.Dispatch<MergeOrUpdateStateAction<DescriptionDetailEditorState>>;
  readonly disableEditor: boolean;
  readonly descriptionMaxLength: number;
}

export function DescriptionEditMode(props: DescriptionEditModeProps): JSX.Element {
  const displayedDescriptionLength = props.displayedDescription? props.displayedDescription.length : 0;
  const charactersRemaining = props.descriptionMaxLength - displayedDescriptionLength;

  const TextAreaStyle: React.CSSProperties = {
    width: '100%',
    resize: 'vertical',
    display: 'block'
  };

  const textAreaMessage = charactersRemaining >= 0 ?
    Messages.remainingCharacters({
      COUNT: charactersRemaining,
      COUNT_LOC: Formatters.formatCount(charactersRemaining)
    }) :
    Messages.characterLimitExceeded({
      COUNT_LOC: Formatters.formatCount(displayedDescriptionLength),
      LIMIT_LOC: Formatters.formatCount(props.descriptionMaxLength),
    });


  return (
    props.inPreviewMode ? (
      <div style={{paddingTop: TabStyles.Spacing.SmallSpace,}}>
        <MarkupField testId={'description-preview'} markup={props.displayedDescription} />
      </div>
    ) : (
      <>
      <TextAreaWidget
        label=""
        testId="description-editor"
        aria-labelledby={sectionHeaderId}
        value={props.displayedDescription}
        onChange={e => props.setEditorState({description: e.target.value})}
        message={textAreaMessage}
        rows={8}
        style={TextAreaStyle}
        className={style({ width: '100%' })}
        disabled={props.disableEditor}
        valid={charactersRemaining >= 0 ? null : false}
      />
      {props.showPreviewAndFormattingHints && <FormattingHints showProjectImageHint={props.showProjectImageHint}/>}
      </>
    )
  );
}

export interface PreviewToggleButtonsProps {
  readonly inPreviewMode: boolean;
  readonly setEditorState: React.Dispatch<MergeOrUpdateStateAction<DescriptionDetailEditorState>>;
  readonly disableButtons: boolean;
}

export function PreviewToggleButton(props: PreviewToggleButtonsProps): JSX.Element {
  const editButton = (
    <ButtonWidget
      buttonType={ButtonType.Outline}
      children={Messages.edit()}
      testId={'exit-preview'}
      handleClick={e => props.setEditorState({previewMode: false})}
      style={{marginBottom: TabStyles.Spacing.MediumSpace}}
      disabled={props.disableButtons}
    />
  );

  const previewButton = (
    <ButtonWidget
      buttonType={ButtonType.Outline}
      children={Messages.preview_action()}
      testId={'description-preview'}
      handleClick={e => props.setEditorState({previewMode: true})}
      style={{marginBottom: TabStyles.Spacing.MediumSpace}}
      disabled={props.disableButtons}
    />
  );

  return props.inPreviewMode ? editButton : previewButton;
}
